import React, { useMemo, useState, useEffect } from "react";
import { ChevronUp, ChevronDown, Filter as FilterIcon, X, Download, Search, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Enterprise DataTable
 * Props:
 *   columns: [{ key, label, render?, sortable?=true, filter?: 'text'|'select'|'range', options?:[], accessor?:(row)=>any, align?:'left'|'right' }]
 *   data:    array of row objects
 *   rowKey:  string (default 'id')
 *   onRowClick: (row) => void
 *   initialSort: { key, dir: 'asc'|'desc' }
 *   testIdPrefix: string
 *   searchableKeys: list of column keys for the global search bar
 *   exportFilename: filename for CSV export
 */
export default function DataTable({
  columns, data, rowKey = "id", onRowClick, initialSort,
  testIdPrefix = "tbl", searchableKeys, exportFilename,
  paginated = false, pageSize: initialPageSize = 25,
  onServerSearch, serverSearchPlaceholder,
}) {
  const [sort, setSort] = useState(initialSort || null);
  const [filters, setFilters] = useState({});
  const [q, setQ] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Point 39/40 — optional server-side search: debounce the query up to the parent so it
  // searches the WHOLE dataset (not just the rows currently loaded on the client).
  useEffect(() => {
    if (!onServerSearch) return;
    const t = setTimeout(() => onServerSearch(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q, onServerSearch]);

  const val = (row, col) => (col.accessor ? col.accessor(row) : (row?.[col.key]));

  const filtered = useMemo(() => {
    let out = data || [];
    // Global search — skipped when server-side search is active (server already matched).
    if (q && !onServerSearch && searchableKeys?.length) {
      const needle = q.toLowerCase();
      out = out.filter((r) => searchableKeys.some((k) => String(r?.[k] ?? "").toLowerCase().includes(needle)));
    }
    // Column filters
    Object.entries(filters).forEach(([k, v]) => {
      if (v == null || v === "") return;
      const col = columns.find((c) => c.key === k);
      if (!col) return;
      if (col.filter === "select") {
        out = out.filter((r) => String(val(r, col) ?? "") === String(v));
      } else if (col.filter === "range") {
        const [min, max] = v;
        if (min != null && min !== "") out = out.filter((r) => Number(val(r, col)) >= Number(min));
        if (max != null && max !== "") out = out.filter((r) => Number(val(r, col)) <= Number(max));
      } else {
        const needle = String(v).toLowerCase();
        out = out.filter((r) => String(val(r, col) ?? "").toLowerCase().includes(needle));
      }
    });
    // Sort
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col) {
        const dir = sort.dir === "asc" ? 1 : -1;
        out = [...out].sort((a, b) => {
          const av = val(a, col); const bv = val(b, col);
          if (av == null && bv == null) return 0;
          if (av == null) return 1; if (bv == null) return -1;
          if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
          return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
        });
      }
    }
    return out;
  }, [data, q, filters, sort, columns, searchableKeys, onServerSearch]);

  // Pagination (opt-in). Reset to first page whenever the result set changes.
  const pageCount = paginated ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  useEffect(() => { setPage(0); }, [q, filters, sort, pageSize, data]);
  const safePage = Math.min(page, pageCount - 1);
  const visibleRows = paginated ? filtered.slice(safePage * pageSize, safePage * pageSize + pageSize) : filtered;

  function toggleSort(col) {
    if (col.sortable === false) return;
    setSort((s) => {
      if (!s || s.key !== col.key) return { key: col.key, dir: "asc" };
      if (s.dir === "asc") return { key: col.key, dir: "desc" };
      return null;
    });
  }

  function clearFilters() { setFilters({}); setQ(""); setSort(null); }

  function exportCsv() {
    const headers = columns.map((c) => c.label);
    const rows = filtered.map((r) => columns.map((c) => {
      const v = val(r, c);
      const s = v == null ? "" : (typeof v === "object" ? JSON.stringify(v) : String(v));
      return `"${s.replace(/"/g, '""')}"`;
    }).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${exportFilename || "export"}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const activeFilterCount = Object.values(filters).filter((v) => v != null && v !== "" && !(Array.isArray(v) && v.every((x) => x == null || x === ""))).length + (q ? 1 : 0);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-table`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {(searchableKeys?.length > 0 || onServerSearch) && (
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={serverSearchPlaceholder || "Search…"} className="pl-8 !py-1.5 !text-[12px] w-56" data-testid={`${testIdPrefix}-search`} />
            </div>
          )}
          <button onClick={() => setShowFilters((s) => !s)} className={`btn-secondary flex items-center gap-1.5 !py-1.5 !text-[12px] ${showFilters ? "!bg-slate-100" : ""}`} data-testid={`${testIdPrefix}-toggle-filters`}>
            <FilterIcon size={12} /> Filters {activeFilterCount > 0 && <span className="bg-teal-700 text-white text-[10px] px-1.5 py-0.5 rounded-full ml-1">{activeFilterCount}</span>}
          </button>
          {activeFilterCount > 0 && <button onClick={clearFilters} className="text-[12px] text-slate-500 hover:text-rose-600 flex items-center gap-1"><X size={11} /> Clear</button>}
        </div>
        <div className="flex items-center gap-2 text-[12px] text-slate-500">
          <span>{filtered.length} of {data?.length || 0}</span>
          <button onClick={exportCsv} className="btn-secondary !py-1.5 !text-[12px] flex items-center gap-1.5" data-testid={`${testIdPrefix}-export`}><Download size={12} /> Export CSV</button>
        </div>
      </div>

      {/* Filter row */}
      {showFilters && (
        <div className="surface p-3 bg-slate-50/50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {columns.filter((c) => c.filter).map((c) => (
              <div key={c.key}>
                <label className="small-caps text-slate-500">{c.label}</label>
                {c.filter === "select" ? (
                  <select className="w-full !py-1.5 !text-[12px]" value={filters[c.key] ?? ""} onChange={(e) => setFilters({ ...filters, [c.key]: e.target.value || null })} data-testid={`${testIdPrefix}-filter-${c.key}`}>
                    <option value="">All</option>
                    {(c.options || []).map((o) => (
                      <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>{typeof o === "string" ? o : o.label}</option>
                    ))}
                  </select>
                ) : c.filter === "range" ? (
                  <div className="flex items-center gap-1">
                    <input type="number" placeholder="min" className="!py-1.5 !text-[12px] w-full" value={(filters[c.key] || ["",""])[0]} onChange={(e) => setFilters({ ...filters, [c.key]: [e.target.value, (filters[c.key] || ["",""])[1]] })} data-testid={`${testIdPrefix}-filter-${c.key}-min`} />
                    <span className="text-slate-400">–</span>
                    <input type="number" placeholder="max" className="!py-1.5 !text-[12px] w-full" value={(filters[c.key] || ["",""])[1]} onChange={(e) => setFilters({ ...filters, [c.key]: [(filters[c.key] || ["",""])[0], e.target.value] })} data-testid={`${testIdPrefix}-filter-${c.key}-max`} />
                  </div>
                ) : (
                  <input className="w-full !py-1.5 !text-[12px]" value={filters[c.key] ?? ""} onChange={(e) => setFilters({ ...filters, [c.key]: e.target.value })} placeholder={`Filter ${c.label.toLowerCase()}…`} data-testid={`${testIdPrefix}-filter-${c.key}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="refined">
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key;
                return (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c)}
                    className={`select-none ${c.sortable === false ? "" : "cursor-pointer hover:bg-slate-100"} ${c.align === "right" ? "text-right" : ""}`}
                    data-testid={`${testIdPrefix}-th-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {c.sortable !== false && (
                        active
                          ? (sort.dir === "asc" ? <ChevronUp size={11} /> : <ChevronDown size={11} />)
                          : <ChevronUp size={11} className="opacity-20" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, idx) => (
              <tr
                key={r[rowKey] ?? idx}
                onClick={() => onRowClick && onRowClick(r)}
                className={onRowClick ? "cursor-pointer" : ""}
                data-testid={`${testIdPrefix}-row-${r[rowKey] ?? idx}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.align === "right" ? "text-right" : ""}>
                    {c.render ? c.render(r) : (val(r, c) ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={columns.length} className="text-center text-slate-400 text-[12px] py-8">No results match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginated && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1" data-testid={`${testIdPrefix}-pagination`}>
          <div className="flex items-center gap-2 text-[12px] text-slate-500">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="!py-1 !text-[12px] !w-auto"
              data-testid={`${testIdPrefix}-page-size`}
            >
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3 text-[12px] text-slate-600">
            <span data-testid={`${testIdPrefix}-page-info`}>
              {filtered.length === 0 ? 0 : safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage <= 0}
                className="btn-secondary !py-1 !px-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`${testIdPrefix}-page-prev`}
              ><ChevronLeft size={14} /></button>
              <span className="px-1.5" data-testid={`${testIdPrefix}-page-current`}>Page {safePage + 1} / {pageCount}</span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={safePage >= pageCount - 1}
                className="btn-secondary !py-1 !px-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`${testIdPrefix}-page-next`}
              ><ChevronRight size={14} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
